import { Check, Clear } from "@mui/icons-material";
import { Box, Divider, Stack } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { usePagination } from "components/hooks";
import StelDataGrid from "components/StelDataGrid";
import VirtualHubIndicator from "components/VirtualHubIndicator";
import { get } from "utils/io";

export default function HubList({ programId }) {
  const theme = useTheme();
  const history = useHistory();

  const { paginationModel, setPaginationModel, totalCount, setTotalCount } =
    usePagination();

  const [hubs, setHubs] = useState([]);

  const dataGridColumns = [
    {
      field: "id",
      flex: 0.5,
      headerName: "Hub ID",
      renderCell: (params) => (
        <>
          {params?.value?.substring(0, 10)}
          {params.row.isVirtual && (
            <Box sx={{ ml: "10px" }}>
              <VirtualHubIndicator />
            </Box>
          )}
        </>
      ),
    },
    {
      field: "connected",
      align: "center",
      flex: 1,
      headerAlign: "center",
      headerName: "Connected",
      renderCell: (params) => {
        if (params.value) {
          return <Check sx={{ color: theme.palette.success.dark }} />;
        }
        return <Clear color="error" />;
      },
      width: 150,
    },
    {
      field: "averageBP",
      flex: 1,
      headerName: "Average BP",
      renderCell: (params) => {
        const bp = params.row.averageBP;
        if (
          !bp ||
          (bp.avg_sys === null && bp.avg_dia === null && bp.avg_bpm === null)
        )
          return "N/A";

        const { avg_sys: sys, avg_dia: dia, avg_bpm: bpm } = bp;
        const bpText =
          sys === null && dia === null
            ? "N/A"
            : `${sys || "N/A"}/${dia || "N/A"} mmHg`;
        const bpmText = `${bpm} bpm` || "N/A";
        return (
          <Stack
            direction="row"
            spacing={1}
            divider={
              <Divider
                orientation="vertical"
                flexItem
                sx={{ bgcolor: "black" }}
              />
            }
            alignItems="center"
            sx={{ minWidth: 165 }}
          >
            <Box>{bpText}</Box>
            <Box>{bpmText}</Box>
          </Stack>
        );
      },
      width: 180,
    },
    {
      field: "timestamp",
      flex: 1,
      headerName: "Last Measurement",
      renderCell: (params) =>
        params?.row?.timestamp
          ? new Date(params.row.timestamp).toLocaleString()
          : "N/A",
      width: 200,
    },
  ];

  useEffect(() => {
    (async () => {
      const res = await get(
        `/programs/${programId}/hubs?limit=${paginationModel.pageSize}&offset=${
          paginationModel.page * paginationModel.pageSize
        }`
      );

      const hubs = res?.data?.hubs || [];
      const totalCount = res?.data?.total_count || 0;

      setHubs(
        hubs.map(
          ({
            connected,
            hub_group: hubGroup,
            version_status: versionStatus,
            is_virtual: isVirtual,
            hub_id: hubId,
            aggregate_values: aggregateValues,
            last_measures: lastMeasures,
          }) => ({
            id: hubId,
            hubId,
            connected,
            hubGroup,
            versionStatus,
            isVirtual,
            averageBP: aggregateValues,
            timestamp: lastMeasures?.last_measurement_time?.timestamp,
          })
        )
      );
      setTotalCount(totalCount);
    })();
  }, [programId, setTotalCount, paginationModel]);

  return (
    <StelDataGrid
      columns={dataGridColumns}
      rows={hubs}
      loading={false}
      onRowClick={({ row }) =>
        history.push(`/programs/${programId}?hub=${row.hubId}`)
      }
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      pageSizeOptions={[20, 25, 50, 100]}
      rowCount={totalCount}
      paginationMode="server"
    />
  );
}

HubList.propTypes = {
  programId: PropTypes.string.isRequired,
};
